import './ServiceEdit.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Services } from '../../api/Services';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { BdaService, BdaServiceProperty } from '../../models/BdaService';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Card, Col, Container, Form, Row, Breadcrumb, Table } from 'react-bootstrap';
import ScriptEditor from '../../components/common/ScriptEditor';
import { cloneDeep, debounce } from 'lodash';
import { accessLevelOptions, buildTree, findInTree, getPropertyIcon, serviceDefinitionNeedsCleaning } from '../../utils';
import IconButton from '../../components/common/IconButton';
import { Flex, Modal, Select, TreeSelect } from 'antd';
import { editor } from 'monaco-editor';
import { AccessLevel } from '../../models/generics';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from "rehype-sanitize";
import ServicePropertyModal from '../../components/generics/ServicePropertyModal';
import { v4 } from 'uuid';
import { Categories } from '../../api/Categories';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';

const SERVICE_NAME_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

interface ValidationError {
  key: string;
  message: string;
}

export default function ServiceEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [cloneMode, setCloneMode] = useState<boolean>();
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [validated, setValidated] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showDefaultValueModal, setShowDefaultValueModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);

  const [service, setService] = useState<BdaService>();
  const [newProperty, setNewProperty] = useState(new BdaServiceProperty('application'));
  const [editing, setEditing] = useState<number>();

  const [category, setCategory] = useState<number>();
  const [categoriesOptions, setCategoriesOptions] = useState<any[]>();

  const [nameError, setNameError] = useState(false);
  const [definitionWarning, setDefinitionWarning] = useState(false);
  const [definitionErrors, setDefinitionErrors] = useState<editor.IMarker[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serviceJSON, setServiceJSON] = useState<string>();
  const [defaultValue, setDefaultValue] = useState("");

  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const disableNonPatchFields = useMemo(() => id !== undefined && !cloneMode, [id, cloneMode]);

  useEffect(() => {
    if (id) retrieveService();
    else setService(new BdaService());

    initCategories();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (service) {
      validateInformation(service);
      const definition = JSON.stringify(service, (_, value) => typeof value === 'undefined' ? null : value, "\t");
      setServiceJSON(definition);
    }
    // eslint-disable-next-line
  }, [service]);

  useEffect(() => {
    if (category)
      setService(curr => {
        const found = findInTree(categoriesOptions || [], category);
        return {
          ...curr!,
          category: found ? { id: found.value, name: found.title } : undefined,
        }
      });
    else setService((curr) => ({ ...curr!, category: undefined }));
  }, [category, categoriesOptions]);

  const retrieveService = () => {
    Services.getService(+id!)
      .then(
        ({ data }) => {
          const mode = searchParams.get("mode");
          setCloneMode(mode === "clone");

          Services.getServiceMarkDown(+id!)
            .then(
              ({ data: markdown }) => setService({ ...data, markDown: { value: markdown } }),
              (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast('service-md-error', message, `${serviceLabels.singular} Markdown Error ${status ? `(${status})` : ''}`, 'danger');
                setService(data);
              }
            );
          setCategory(data.category?.id);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-error', message, `${serviceLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initCategories = () => {
    const params = new URLSearchParams([['populateHierarchy', 'true']]);

    Categories.getCategories(params)
      .then(
        ({ data }) => setCategoriesOptions(buildTree(data)),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          setCategoriesOptions([]);
          showToast('categories-error', message, `Categories Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openDefinitionModal = () => setShowDefinitionModal(true);
  const handlePropertyModalClose = () => {
    setShowPropertyModal(false);
    setEditing(undefined);
  }
  const handleDefinitionModalClose = () => setShowDefinitionModal(false);
  const handleAddProperty = () => {
    setNewProperty(new BdaServiceProperty('application'));
    setShowPropertyModal(true);
  }

  const handleSubmitProperty = (values: any) => {
    setService(curr => {
      const newProperties = cloneDeep(curr!.properties);
      if (editing !== undefined) newProperties[editing] = values;
      else newProperties.push(values);
      return { ...curr!, properties: newProperties };
    });
    setNewProperty(new BdaServiceProperty('application'));
    handlePropertyModalClose();
  }

  const cancel = () => navigate('/services');

  const handleFieldChange = (field: keyof BdaService) => (evt: any) => {
    const value = (field === 'category' || field === 'accessLevel' || field === 'tags') ? evt : evt.target.value;
    if (field === "category") setCategory(value);
    setService({
      ...service!,
      [field]: value,
    });
    if (field === "name") {
      if (!SERVICE_NAME_REGEX.test(value) || value.length > 30) setNameError(true);
      else setNameError(false);
    }
  };

  const sendRequest = useCallback(() => {
    showLoader();

    const body = { ...service! };
    // TODO remove this
    body.mode = 'BATCH';
    if (!body.accessLevel) body.accessLevel = AccessLevel.PUBLIC;
    if (category !== undefined) {
      const selectedCategory = findInTree(categoriesOptions || [], category);
      body.category = { id: selectedCategory!.value, name: selectedCategory!.title };
    }

    const promise = id
      ? cloneMode
        ? Services.updateService(body!)
        : Services.updateServiceFields(body!)
      : Services.createService(body!);

    promise
      .then(
        ({ data }) => {
          hideLoader();
          showToast('services-success', `${serviceLabels.singular} ${data.name} ${id ? 'updated' : 'created'} successfully.`, 'Success', 'success');
          navigate(`/services/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('services-error', message, `${serviceLabels.singular} ${id ? 'Update' : 'Create'} Error ${status ? ` (${status})` : ''}`, 'danger');
        }
      );
  }, [service, showLoader, hideLoader, showToast, id, cloneMode, navigate, category, categoriesOptions, serviceLabels]);

  const submitService = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();

    setValidated(true);

    if (!service!.accessLevel || nameError) return;
    if (form.checkValidity() === true) sendRequest();
  }, [service, sendRequest, nameError]);

  const submitDefinition = () => {
    const newService = JSON.parse(serviceJSON!);
    if (!newService.properties) newService.properties = [];
    setService(newService);
    handleDefinitionModalClose();
  }

  const editProperty = (index: number) => () => {
    setEditing(index);
    setNewProperty({ ...service!.properties[index] });
    setShowPropertyModal(true);
  }

  const cloneProperty = (index: number) => () => {
    const cloned = cloneDeep(service!.properties[index]);
    cloned.key += `-${v4().substring(0, 6)}`;
    setService({
      ...service!,
      properties: service!.properties.concat(cloned),
    });
  }

  const deleteProperty = (index: number) => () => {
    const newProperties = [...service!.properties];
    newProperties.splice(index, 1);
    setService({
      ...service!,
      properties: newProperties
    });
  }

  const handleDefaultValueModalClose = useCallback(() => {
    setDefaultValue("");
    setShowDefaultValueModal(false);
  }, []);
  const openDefaultValueModal = useCallback((property: BdaServiceProperty) => () => {
    setDefaultValue(property.defaultValue);
    setShowDefaultValueModal(true);
  }, []);

  const renderServiceProperties = () => {
    if (!service!.properties.length) return (
      <Alert className="py-2" variant="primary">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No properties found.</span>
      </Alert>
    )

    return (
      <Table className="mb-0">
        <thead>
          <tr>
            <th style={{ width: 20 }}></th>
            <th style={{ width: '25%' }}>Key</th>
            <th className="text-center">Type</th>
            <th className="text-center">Default Value</th>
            <th style={{ width: 20 }} className="text-center">Mandatory</th>
            {!disableNonPatchFields && <th className="text-center">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {service!.properties.map((property, index) => (
            <tr key={`prop-${property.key}`}>
              <td>
                <FontAwesomeIcon icon={getPropertyIcon(property.type)} />
              </td>
              <td>{property.key}</td>
              <td className="text-center">{property.valueType}</td>
              <td className="text-center">
                {property.valueType === 'JSON'
                  ? (
                    <Button size="sm" variant="light" className="border" onClick={openDefaultValueModal(property)}>
                      <FontAwesomeIcon icon={solid('eye')} />
                      <span className="ms-2">Show Value</span>
                    </Button>
                  )
                  : property.defaultValue
                }
              </td>
              <td className="text-center">{property.mandatory && <FontAwesomeIcon icon={solid('check-square')} />}</td>
              {!disableNonPatchFields && (
                <td className="text-center">
                  <IconButton type="button" className="ms-2" onClick={cloneProperty(index)} tooltip="Clone">
                    <FontAwesomeIcon icon={regular('clone')} />
                  </IconButton>
                  <IconButton type="button" onClick={editProperty(index)} tooltip="Edit">
                    <FontAwesomeIcon icon={solid('pen')} />
                  </IconButton>
                  <IconButton type="button" className="text-danger ms-2" onClick={deleteProperty(index)} tooltip="Remove">
                    <FontAwesomeIcon icon={solid('times')} />
                  </IconButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  const handleMarkdownChange = useCallback((value?: string) => {
    setService(previous => ({
      ...previous!,
      markDown: {
        value: value || "",
      }
    }))
  }, []);

  const validateInformation = useCallback((newService: BdaService) => {
    const errors = [];
    if (!newService.name || newService.name === "") errors.push({ key: 'name', message: 'is required and cannot be empty' });
    else if (!SERVICE_NAME_REGEX.test(newService.name) || newService.name.length > 30) errors.push({ key: 'name', message: "can include just numbers, letters and dash ('-') and must be under 30 characters." });
    // if (!newService.category) errors.push({ key: 'category', message: 'is required and cannot be empty' });
    if (!newService.version || newService.version === "") errors.push({ key: 'version', message: 'is required and cannot be empty' });
    if (!newService.url || newService.url === "") errors.push({ key: 'url', message: 'is required and cannot be empty' });
    if (!newService.accessLevel || newService.url === "") errors.push({ key: 'access level', message: 'is required and cannot be empty' });
    setErrors(errors);
  }, []);

  // TODO should we improve this?
  const cleanDefinition = useCallback(() => {
    setServiceJSON(curr => {
      const newService = JSON.parse(curr!) as BdaService;
      delete newService.id;
      delete newService.ref;
      delete newService.label;
      delete newService.statusModel;
      delete newService.models;
      delete newService.organization;
      delete newService.owner;
      newService.properties?.forEach(prop => delete prop.id);
      return JSON.stringify(newService, (_, value) => typeof value === 'undefined' ? null : value, "\t");;
    });
    setDefinitionWarning(false);
  }, []);

  const parseDefinition = useCallback((value?: string) => {
    setServiceJSON(value);
    try {
      const newService: BdaService = JSON.parse(value || "null");
      if (newService && serviceDefinitionNeedsCleaning(newService)) setDefinitionWarning(true);
      else setDefinitionWarning(false);
      validateInformation(newService);
    } catch (e) {
      console.log('JSON is not valid', e);
    }
  }, [validateInformation]);

  const debounceParsing = useMemo(() => debounce(parseDefinition, 1000), [parseDefinition]);

  // cleanup debounced handler
  useEffect(() => {
    return () => debounceParsing.cancel();
  }, [debounceParsing]);

  const handleJSONErrors = (errors: editor.IMarker[]) => setDefinitionErrors(errors || [])

  const renderServiceForm = () => {
    return (
      <Form noValidate validated={validated} onSubmit={submitService}>
        <Card.Body className="service-form-wrapper">
          <Row>
            <Col lg={6}>
              <Row className="gx-2 mb-3">
                <Col>
                  <Form.Group controlId="service-name" className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control size="sm" placeholder={`${serviceLabels.singular} Name *`} value={service!.name} onChange={handleFieldChange('name')} required />
                    <Form.Control.Feedback type="invalid">{serviceLabels.singular} name is required.</Form.Control.Feedback>
                    {nameError && <Form.Control.Feedback type="invalid" className="d-block">{serviceLabels.singular} name can include just numbers, letters and dash ('-') and must be under 30 characters.</Form.Control.Feedback>}
                  </Form.Group>

                  <Form.Group controlId="service-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={4} placeholder={`${serviceLabels.singular} description`} value={service!.description} onChange={handleFieldChange('description')}></Form.Control>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="service-version" className="mb-3">
                    <Form.Label>Version</Form.Label>
                    <Form.Control size="sm" placeholder={`${serviceLabels.singular} Version *`} value={service!.version} onChange={handleFieldChange('version')} required disabled={disableNonPatchFields} />
                    <Form.Control.Feedback type="invalid">{serviceLabels.singular} Version is required.</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Access Level</Form.Label>
                    <Select
                      allowClear
                      className="w-100"
                      placeholder={`Select ${serviceLabels.singular} access level *`}
                      value={service!.accessLevel}
                      onChange={handleFieldChange("accessLevel")}
                      disabled={!!id}
                    >
                      {accessLevelOptions
                        .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                        .map((option, index) => (
                          <Select.Option key={index} value={option.value} label={option.label}>
                            <div>
                              <FontAwesomeIcon icon={option.icon} fixedWidth />
                              <span className="ms-2">{option.label}</span>
                            </div>
                          </Select.Option>
                        ))
                      }
                    </Select>
                    {validated && !service!.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">{serviceLabels.singular} access level is required.</Form.Control.Feedback>}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <TreeSelect
                      allowClear
                      loading={!categoriesOptions}
                      className="w-100"
                      placeholder={`Select ${serviceLabels.singular} category`}
                      treeData={categoriesOptions}
                      value={category}
                      onChange={handleFieldChange('category')}
                      treeNodeFilterProp="title"
                      showSearch
                    />
                    {/* {validated && !category && <Form.Control.Feedback className="d-block" type="invalid">Service category is required.</Form.Control.Feedback>} */}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="service-url" className="mb-3">
                <Form.Label>Url</Form.Label>
                <Form.Control size="sm" placeholder={`${serviceLabels.singular} URL *`} type="url" value={service!.url} onChange={handleFieldChange('url')} required disabled={disableNonPatchFields} />
                <Form.Control.Feedback type="invalid">{serviceLabels.singular} Url is required.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="service-tags" className="mb-3">
                <Form.Label>Tags</Form.Label>
                <Select mode="tags" className="w-100" placeholder={`${serviceLabels.singular} Tags`} value={service!.tags} onChange={handleFieldChange('tags')} />
              </Form.Group>

              <Flex align="center" justify="space-between" className="mb-2">
                <h6 className="mb-0">{serviceLabels.singular} Properties</h6>

                {!disableNonPatchFields && (
                  <Button size="sm" variant="light" className="border" onClick={handleAddProperty}>
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Add property</span>
                  </Button>
                )}
              </Flex>
              {service!.properties && renderServiceProperties()}
            </Col>

            <Col lg={6} className="mt-3 mt-lg-0">
              <Flex vertical className="h-100">
                <h6 className="d-lg-none">{serviceLabels.singular} Markdown</h6>
                <Col data-color-mode="light">
                  <MDEditor
                    value={service!.markDown?.value || ""}
                    onChange={handleMarkdownChange}
                    previewOptions={{
                      rehypePlugins: [[rehypeSanitize]],
                    }}
                    textareaProps={{ placeholder: `Enter ${serviceLabels.singular} markdown here.` }}
                    height="100%"
                  />
                </Col>
              </Flex>
            </Col>
          </Row>
        </Card.Body>

        <Flex align="center" justify="space-between" className="border-top p-2 px-3">
          <Button type="button" variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button type="submit" variant="success">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </Flex>
      </Form>
    )
  }

  const renderServiceEditor = () => {
    return (
      <Row className="gx-2 align-items-start">
        <Col md={8}>
          <ScriptEditor
            value={serviceJSON}
            onChange={debounceParsing}
            onValidated={handleJSONErrors}
          />
        </Col>

        <Col md={4}>
          {definitionWarning && (
            <Alert variant="warning" className="py-2 mb-2">
              <FontAwesomeIcon icon={solid('exclamation-triangle')} />
              <span className="ms-2">{serviceLabels.singular} definition needs to be cleaned.</span>

              <Flex justify="center" className="mt-2">
                <Button variant="warning" size="sm" onClick={cleanDefinition}>
                  <FontAwesomeIcon icon={solid('brush')} />
                  <span className="ms-2">Clean Definition</span>
                </Button>
              </Flex>
            </Alert>
          )}

          {definitionErrors?.map(({ message, startLineNumber, startColumn, endLineNumber, endColumn }, index) => (
            <Alert className="py-2 mb-2" key={index} variant="danger">
              <FontAwesomeIcon icon={solid('exclamation-circle')} />
              <b className="ms-2">[{startLineNumber}:{startColumn} - {endLineNumber}:{endColumn}]</b>
              <span className="ms-2">{message}</span>
            </Alert>
          ))}

          {errors?.map((err, index) => (
            <Alert className="py-2 mb-2" key={index} variant="danger">
              <FontAwesomeIcon icon={solid('exclamation-circle')} />
              <b className="ms-2">{err.key}</b>
              <span className="ms-2">{err.message}</span>
            </Alert>
          ))}

          {(!definitionErrors || definitionErrors.length === 0) && (!errors || errors.length === 0) && (
            <Alert variant="success" className="py-2">
              <FontAwesomeIcon icon={solid('check-circle')} />
              <span className="ms-3">Definition looks good.</span>
            </Alert>
          )}
        </Col>
      </Row>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-circle')} />
        <span className="ms-2">An error occurred, try again later.</span>
      </Alert>
    )

    return service
      ? renderServiceForm()
      : (
        <div className="p-5 text-center">
          <AlidaLoader size={350} />
        </div>
      );
  }

  return (
    <Container className="mt-3" fluid>
      <Helmet>
        <title>{serviceLabels.singular} {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>{serviceLabels.plural}</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/services/${id}` }}>{service?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>{serviceLabels.singular} Edit</Breadcrumb.Item>
            </>
          )
          : <Breadcrumb.Item active>New {serviceLabels.singular}</Breadcrumb.Item>
        }
      </Breadcrumb>

      <Card className="mb-3">
        <Flex align="center" className="border-bottom p-2 px-3">
          <Col>
            <h5 className="mb-1">{id ? `${serviceLabels.singular} Edit` : `New ${serviceLabels.singular}`}</h5>
            <h6 className="text-muted mb-0">{id ? `Edit existing ${serviceLabels.singular}.` : `Register new ${serviceLabels.singular} below.`}</h6>
          </Col>

          {(!id || cloneMode) && (
            <Button size="sm" variant="light" className="w-auto border" onClick={openDefinitionModal}>
              <FontAwesomeIcon icon={solid('code')} />
              <span className="ms-2">{serviceLabels.singular} Definition</span>
            </Button>
          )}
        </Flex>

        {renderAsyncContent()}
      </Card>

      <Modal
        title="Property Default Value"
        open={showDefaultValueModal}
        onClose={handleDefaultValueModalClose}
        onCancel={handleDefaultValueModalClose}
        footer={
          <Flex justify="center">
            <Button variant="light" size="sm" className="border" onClick={handleDefaultValueModalClose}>Cancel</Button>
          </Flex>
        }
      >
        <div className="mt-3">
          <ScriptEditor value={defaultValue} readOnly />
        </div>
      </Modal>

      <ServicePropertyModal
        open={showPropertyModal}
        onClose={handlePropertyModalClose}
        onSubmit={handleSubmitProperty}
        property={newProperty}
      />

      <Modal
        open={showDefinitionModal}
        onClose={handleDefinitionModalClose}
        onCancel={handleDefinitionModalClose}
        title={`${serviceLabels.singular} Definition`}
        width={1000}
        footer={
          <Flex align="center" justify="space-between">
            <Button size="sm" variant="light" className="border" onClick={handleDefinitionModalClose}>Cancel</Button>
            <Button size="sm" variant="success" disabled={errors.length > 0 || definitionErrors.length > 0} onClick={submitDefinition}>Confirm</Button>
          </Flex>
        }
      >
        {renderServiceEditor()}
      </Modal>
    </Container>
  )
}
